// @ts-nocheck

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CompaniesState } from '@/types/companies';
import {
  getCompanies,
  getCompany,
  createCompany,
  updateCompany,
  deleteCompany,
  getCompanyContacts,
  createCompanyContact,
  updateCompanyContact,
  deleteCompanyContact,
  getCompanyStudies,
} from './thunks';

const initialState: CompaniesState = {
  companies: [],
  currentCompany: null,
  contacts: [],
  studies: [],
  isLoading: false,
  error: null,
  hasMore: true,
  offset: 0,
  limit: 100,
  isLoadingMore: false,
  total: 0,
};

export const companiesSlice = createSlice({
  name: 'companies',
  initialState,
  reducers: {
    setCurrentCompany: (state, action: PayloadAction<CompanySchema | null>) => {
      state.currentCompany = action.payload;
    },
    clearError: state => {
      state.error = null;
    },
    resetCurrentCompany: state => {
      state.currentCompany = null;
    },
    resetContacts: state => {
      state.contacts = [];
    },
    resetPagination: state => {
      state.offset = 0;
      state.hasMore = true;
      state.companies = [];
    },
  },
  extraReducers: builder => {
    builder
      // Get Companies
      .addCase(getCompanies.pending, (state, action) => {
        if (action.meta.arg?.offset === 0 || !action.meta.arg?.offset) {
          state.isLoading = true;
        } else {
          state.isLoadingMore = true;
        }
        state.error = null;
      })
      .addCase(getCompanies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        state.error = null;
        const { offset = 0 } = action.payload;

        if (offset === 0) {
          state.companies = action.payload.data;
          state.offset = action.payload.data.length;
        } else {
          state.companies = [...state.companies, ...action.payload.data];
          state.offset = state.offset + action.payload.data.length;
        }

        // Use total count to determine if there's more data
        state.hasMore = state.companies.length < action.payload.total;

        // Store the total Companies from API
        state.totalCompanies = action.payload.total;

        console.log('Redux updated:', {
          totalCompanies: state.companies.length,
          newOffset: state.offset,
          hasMore: state.hasMore,
          receivedCount: action.payload.data.length,
          totalAvailable: action.payload.total,
        });
      })
      .addCase(getCompanies.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        state.error = action.payload as string;
      })

      // Get Company
      .addCase(getCompany.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCompany.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.currentCompany = action.payload;
      })
      .addCase(getCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Create Company
      .addCase(createCompany.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createCompany.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(createCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update Company
      .addCase(updateCompany.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Delete Company
      .addCase(deleteCompany.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCompany.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(deleteCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Get Company Contacts
      .addCase(getCompanyContacts.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCompanyContacts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.contacts = action.payload;
      })
      .addCase(getCompanyContacts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Create Company Contact
      .addCase(createCompanyContact.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createCompanyContact.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(createCompanyContact.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update Company Contact
      .addCase(updateCompanyContact.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCompanyContact.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updateCompanyContact.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Delete Company Contact
      .addCase(deleteCompanyContact.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCompanyContact.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(deleteCompanyContact.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Get Company Studies
      .addCase(getCompanyStudies.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCompanyStudies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.studies = action.payload;
      })
      .addCase(getCompanyStudies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setCurrentCompany,
  clearError,
  resetCurrentCompany,
  resetContacts,
  resetPagination,
} = companiesSlice.actions;

export const companiesReducer = companiesSlice.reducer;
